import { useTranslation } from 'react-i18next';

import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';

import Button from '~/components/Button';
import Pagination from '~/components/Pagination';
import PostCardv2 from '~/components/PostCardv2';
import PostCardv3 from '~/components/PostCardv3';
import Typography from '~/components/Typography';
import useDebounce from '~/hooks/useDebounce';
import useFilterPosts from '~/hooks/useFilterPosts';
import usePostsInsights from '~/hooks/useLoaders/usePostsInsights';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Post } from '~/interfaces/post';

import SectionHeading from '../SectionHeading';
import ArchiveFilter, { ClearAction } from './ArchiveFilter';
import FeedPostsLoading from './FeedPostsLoading';

interface Props {
  cardOrientation?: 'horizontal' | 'vertical';
  gridCols?: '2' | '3';
  isNewWebBuilder?: boolean;
}

const ArchiveSection = ({ cardOrientation = 'horizontal', gridCols = '3', isNewWebBuilder = false }: Props) => {
  const { t } = useTranslation();

  const { paginatedPosts, homePage, publication, cardStyles } = useTypedLoaderData<any>();
  const card = homePage?.posts_section?.card;
  const color = homePage.general_section.theme.text_color;
  const font = homePage.general_section.theme.font;
  const titleColor = homePage.posts_section.archive.header_text_color ?? homePage.general_section.theme.accent_color;
  const isPagination = homePage?.posts_section?.archive.load_more_posts_type === 'pagination';
  const archiveTitle = homePage?.posts_section?.archive.header_text || 'Archive';
  const showArchiveHeader = homePage?.posts_section?.archive.show_archive_header || false;
  const headerFontSize = homePage?.posts_section?.archive.font_size || '4xl';

  const { searchTerm, setSearchTerm, isSearching, setIsSearching } = useDebounce(500);

  const { isLoading, fetchPosts, postsToRender, showLoadMore } = useFilterPosts({
    initialPosts: paginatedPosts.posts,
    initialPagination: paginatedPosts.pagination,
    publication,
  });

  const { postsWithInsights } = usePostsInsights({
    incomingPosts: postsToRender,
  });

  const renderNoPosts = () => {
    return (
      <div className="mt-12 flex w-full flex-col items-center justify-center gap-4">
        <MagnifyingGlassIcon className="h-8 w-8" />
        <Typography
          as="p"
          size="xl"
          fontFamily={card.font}
          fontFamilyv2={isNewWebBuilder ? card.font : undefined}
          color={card.text_color}
          className="text-xl font-semibold"
        >
          {t('search.posts.no_results')}
        </Typography>
        <ClearAction setSearchTerm={setSearchTerm} color={color} classNames="p-2 px-4 text-base" />
      </div>
    );
  };

  const loadMoreTextColor = homePage?.posts_section.archive.button_text_color || '';
  const loadMoreBorderColor = homePage?.general_section.theme.border_color || '';
  const loadMoreBGColor = homePage?.posts_section.archive.button_bg_color || '';

  return (
    <div>
      {isNewWebBuilder && showArchiveHeader && (
        <div className="mb-4">
          <SectionHeading
            title={archiveTitle}
            fontFamily={font}
            accentColor={titleColor}
            textColor={color}
            isNewWebBuilder={isNewWebBuilder}
            fontSize={headerFontSize}
          />
        </div>
      )}
      <ArchiveFilter
        publication={publication}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        isNewWebBuilder={isNewWebBuilder}
      />

      {isSearching ? (
        <FeedPostsLoading />
      ) : (
        <>
          {postsWithInsights.length === 0 ? (
            <div>{renderNoPosts()}</div>
          ) : (
            <>
              <div
                className={cx({
                  'flex flex-col space-y-6': cardOrientation === 'horizontal',
                  'grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3':
                    cardOrientation === 'vertical' && gridCols === '3',
                  'grid grid-cols-1 gap-6 md:grid-cols-2': cardOrientation === 'vertical' && gridCols === '2',
                })}
              >
                {postsWithInsights?.map((post: Post, _index: number) => {
                  if (isNewWebBuilder) {
                    return (
                      <PostCardv3 key={post.id} post={post} cardStyles={cardStyles} orientation={cardOrientation} />
                    );
                  }

                  return <PostCardv2 key={post.id} post={post} card={card} orientation={cardOrientation} />;
                })}
              </div>
              {!isPagination && showLoadMore && (
                <div className="mt-6 flex w-full justify-center">
                  <Button
                    style={{
                      backgroundColor: loadMoreBGColor,
                      borderColor: loadMoreBorderColor,
                      color: loadMoreTextColor,
                    }}
                    className={cx('transition-all group-hover:brightness-110')}
                    onClick={fetchPosts}
                  >
                    {isLoading ? `${t('shared.actions.loading')}...` : t('shared.cta.load_more')}
                  </Button>
                </div>
              )}
              {isPagination && (
                <Pagination
                  homePage={homePage}
                  isNewWebBuilder={isNewWebBuilder}
                  buttonBGColor={loadMoreBGColor}
                  buttonBorderColor={loadMoreBorderColor}
                  buttonTextColor={loadMoreTextColor}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ArchiveSection;
